import React, { useEffect, useEffectEvent, useRef } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { randomUUID as uuidv4 } from 'expo-crypto';
import { useRouter } from 'expo-router';
import { IPaymentMethod } from 'features/payment/types';
import { mapPaymentMethodToLoyaltyPaymentMethods } from 'features/payment/utils';
import { useStoreContext } from 'features/store';
import { StyleSheet } from 'react-native';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { Box } from 'components/ui';
import { useCart } from 'hooks/use-cart';
import { isHydrationOrDehydrationInProgress } from 'hooks/use-cart-hydration.ts';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { CartItem } from 'pages/cart/cart-item';
import { PromoMessageRibbon } from 'pages/loyalty/loyalty-promo-message-ribbon';
import { useCartContext } from 'state/cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { sumAppliedTimesApplied } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getServerOrderSubtotalBeforeOfferDiscounts } from 'state/loyalty/hooks/utils/helpers';
import { useRemoveExpiredOfferFromCart } from 'state/loyalty/offers/hooks/use-remove-expired-offer-from-cart';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { spacing } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { CartEntryType } from 'utils/cart/types';
import { routes } from 'utils/routing';

import Totals from '../../../experiments/checkout-v2/control/pages/cart/your-cart/totals';
import { useLoyaltyCartEntriesEvaluation } from '../hooks/use-loyalty-cart-entries-evaluation';
import { Upsell } from '../upsell';

import DonationContainer from './donation-container';
import FoodwareContainer from './foodware-container';
import PriceMismatchInternalOnly from './price-mismatch-internal-only';
import { ActionButtonsContainer, ActionLinkContainer } from './styled';
import YourCartHeading from './your-cart-heading';

interface IYourCartProps {
  checkoutPaymentMethod?: IPaymentMethod | null;
  readOnly?: boolean;
  showAddItemsBtn?: boolean;
  showCartPromoCodes?: boolean;
}

const YourCart: React.FC<React.PropsWithChildren<IYourCartProps>> = ({
  checkoutPaymentMethod,
  readOnly = false,
  showAddItemsBtn = true,
}) => {
  const { cartEntries, calculateCartTotalWithDiscount, addItemToCart } = useCart();
  const isDonationsEnabled = useFlag(LaunchDarklyFlag.ENABLE_FOUNDATION_DONATIONS);
  const isDesktop = useIsDesktopBreakpoint();
  const router = useRouter();

  const {
    pricedAndAvailable = true,
    priceOrderSuccess,
    priceMutationError,
    serverOrder,
    shouldSkipPriceOrder,
    a11yMessage,
    announceA11yCartMessage,
    isOrderPriceLoading,
  } = useCartContext();

  const { loyaltyUser } = useLoyaltyUser();
  const { ensureSortedOffersLoaded } = useLoyaltyContext();
  const evaluateLoyaltyCartEntries = useLoyaltyCartEntriesEvaluation();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const dispatch = useAppDispatch();
  const { store } = useStoreContext();

  const appliedRewardsCount = useRef(0);
  const cartEntriesCount = useRef(0);

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount
    ? calculateCartTotalWithDiscount()
    : {};

  const positiveCartTotal = isCartTotalNegative
    ? 0
    : getServerOrderSubtotalBeforeOfferDiscounts(serverOrder);

  useEffect(() => {
    // Resetting offer feedback on mount to avoid carrying over feedback from offer list
    dispatch(actions.loyalty.resetOfferFeedbackMap());
  }, [dispatch]);

  // Lazy-load sortedSystemwideOffers when Cart mounts so offer validation/display has data
  useEffect(() => {
    ensureSortedOffersLoaded();
  }, [ensureSortedOffersLoaded]);

  useEffect(() => {
    /**
     * If an unauthenticated user adds items to their cart and then login,
     * they'll likely be part of the Cart v2 experiment.
     * In this case, we'll need to adjust cart entries with quantities greater than 1 to fit the new Cart v2 flow.
     */

    const cartEntriesToBeExpanded = cartEntries.filter(item => item.quantity && item.quantity > 1);

    if (cartEntriesToBeExpanded.length) {
      const expandedCartEntries: ILegacyCartEntry[] = [];

      cartEntriesToBeExpanded.forEach(item => {
        const expandedCartEntry = [...Array(item.quantity)].map(() => {
          const newId = uuidv4();
          return {
            ...item,
            quantity: 1,
            cartId: newId,
            lineId: newId,
          };
        });
        expandedCartEntries.push(...expandedCartEntry);
      });

      expandedCartEntries.forEach(item => {
        dispatch(actions.ordering.addCartEntry(item));
      });

      // Remove the original expanded cart entries to improve the order of the cart items.
      dispatch(
        actions.ordering.removeCartEntries(
          new Set(cartEntriesToBeExpanded.map(item => item.cartId))
        )
      );
    }
  }, [addItemToCart, cartEntries, dispatch]);

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const {
    isLoading: isUnifiedMenuLoading,
    loadedStoreNumber,
    hasError: unifiedMenuHasError,
  } = useUnifiedMenuContext();
  const isCurrentStoreMenuFetched = Boolean(store?.number) && loadedStoreNumber === store.number;
  const shouldWaitForUnifiedMenuEvaluation =
    Boolean(store?.number) &&
    isUnifiedMenuEnabled &&
    !unifiedMenuHasError &&
    !isCurrentStoreMenuFetched &&
    (isUnifiedMenuLoading || loadedStoreNumber !== store.number);

  const runIncentivesEvaluation = useEffectEvent((subtotalAmount: number) => {
    if (loyaltyUser?.id) {
      const paymentMethod = mapPaymentMethodToLoyaltyPaymentMethods(checkoutPaymentMethod);

      evaluateLoyaltyCartEntries(loyaltyUser?.id, cartEntries, subtotalAmount, paymentMethod);
    }
  });

  useEffect(() => {
    if (
      shouldWaitForUnifiedMenuEvaluation ||
      !priceOrderSuccess ||
      priceMutationError ||
      // Wait for a priced server order; reading the subtotal from a null/in-flight
      // server order (during a reprice) would send subtotalAmount: 0 and break
      // min-spend offer evaluation.
      !serverOrder?.cart
    ) {
      return;
    }

    // Pass the subtotal from this render's (guard-consistent) closure instead of
    // letting the effect event read a later, reprice-in-progress value.
    runIncentivesEvaluation(positiveCartTotal);
    // runIncentivesEvaluation is an Effect Event and must stay out of dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    priceMutationError,
    priceOrderSuccess,
    shouldWaitForUnifiedMenuEvaluation,
    serverOrder?.cart?.subTotalCents,
  ]);

  useEffect(() => {
    if (pricedAndAvailable) {
      const validCartEntriesCount =
        // Donation and Extras entries should not be taken into account for querying offers
        cartEntries?.filter(({ isExtra, isDonation }: ILegacyCartEntry) => !isDonation && !isExtra)
          .length ?? 0;
      const appliedRewardsLength = sumAppliedTimesApplied(appliedLoyaltyRewards);

      appliedRewardsCount.current = appliedRewardsLength;
      cartEntriesCount.current = validCartEntriesCount;
    }
  }, [appliedLoyaltyRewards, cartEntries, pricedAndAvailable]);

  // TODO: https://rbictg.atlassian.net/browse/BKPE-25114
  useRemoveExpiredOfferFromCart();

  const appliedComboReward = cartEntries.find(cartEntry => {
    return (
      isUnifiedMenuEnabled &&
      appliedLoyaltyRewards[cartEntry.cartId]?.timesApplied > 0 &&
      cartEntry.type === CartEntryType.combo
    );
  });

  return (
    <Box>
      <YourCartHeading />

      <PromoMessageRibbon cartEntries={cartEntries} rounded={true} />

      <Box accessibilityLabel="cart-section-heading-label">
        <Box
          accessible={false}
          accessibilityLabel={a11yMessage}
          accessibilityLiveRegion="polite"
          style={styles.accessibilityMessageAnnouncer}
          testID="cart-accessibility-message-announcer"
        />

        <Box testID="cart-items">
          {cartEntries.map((item: ILegacyCartEntry) => (
            <CartItem
              item={item}
              key={item.cartId || item._id}
              readOnly={readOnly}
              rewardApplied={appliedLoyaltyRewards[item.cartId]?.timesApplied > 0}
              showReadOnlyQuantity
              onAnnounceA11yMessage={announceA11yCartMessage}
              appliedComboReward={appliedComboReward}
            />
          ))}
        </Box>

        {showAddItemsBtn && (
          <ActionButtonsContainer>
            <ActionLinkContainer>
              <ActionButton
                onPress={() => router.navigate(routes.menu)}
                startIcon={<Icon variant="add" {...hiddenAccessibilityPlatformProps} />}
                variant={ActionButtonVariants.OUTLINE}
                eventAttributes={{
                  Name: t('addItemsLowerCase'),
                }}
                disabled={isOrderPriceLoading}
              >
                {t('addItems')}
              </ActionButton>
            </ActionLinkContainer>
          </ActionButtonsContainer>
        )}

        <Box style={isDesktop ? styles.addOnsContainerDesktop : styles.addOnsContainer}>
          <Upsell isInCart />
          <FoodwareContainer />
          {isDonationsEnabled && <DonationContainer isLoading={isOrderPriceLoading} />}
        </Box>

        <PriceMismatchInternalOnly />

        {pricedAndAvailable && (
          <Totals
            isLoading={isOrderPriceLoading || isHydrationOrDehydrationInProgress}
            serverOrder={serverOrder}
            shouldSkipPriceOrder={shouldSkipPriceOrder}
          />
        )}
      </Box>
    </Box>
  );
};

export default React.memo(YourCart);

const styles = StyleSheet.create({
  accessibilityMessageAnnouncer: {
    height: 0,
    opacity: 0,
    width: 0,
  },
  addOnsContainer: {
    marginTop: spacing.$2,
  },
  addOnsContainerDesktop: {
    marginTop: spacing.$4,
  },
});
